import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, buildQuery, editionsOf, errorMessage, type Customer, type License, type Page, type Product, type ProductDetail } from '../lib/api';
import { useAsync, useDebounced } from '../lib/hooks';
import { fmtDate } from '../lib/format';
import { ErrorAlert, Field, LastSeen, LicenseStatusBadge, Modal, PageHeader, Pagination, Spinner, TypeBadge } from '../components/ui';
import { useT } from '../i18n';

const PAGE_SIZE = 25;
const TYPES = ['perpetual', 'subscription', 'trial'] as const;
const MODES = ['node_locked', 'floating'] as const;
const STATUSES = ['active', 'suspended', 'revoked', 'expired'] as const;
const INACTIVE_DAYS = [7, 30, 90] as const;

export function LicensesPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q);
  const [creating, setCreating] = useState(false);
  const page = Number(params.get('page') ?? 1);
  const filters = {
    q: dq,
    product_id: params.get('product_id') ?? '',
    customer_id: params.get('customer_id') ?? '',
    edition_id: params.get('edition_id') ?? '',
    status: params.get('status') ?? '',
    type: params.get('type') ?? '',
    mode: params.get('mode') ?? '',
    expiring_within_days: params.get('expiring_within_days') ?? '',
    inactive_days: params.get('inactive_days') ?? '',
    never_activated: params.get('never_activated') ?? '',
    sort: params.get('sort') ?? '',
  };
  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.delete('page');
    setParams(next);
  };
  const products = useAsync(() => api.get<Product[]>('/v1/admin/products'), []);
  // Las ediciones son de cada producto: el filtro aparece al elegir uno.
  const filterProduct = useAsync(
    () => (filters.product_id ? api.get<ProductDetail>(`/v1/admin/products/${filters.product_id}`) : Promise.resolve(null)),
    [filters.product_id],
  );
  const list = useAsync(
    () => api.get<Page<License>>(`/v1/admin/licenses${buildQuery({ ...filters, page, page_size: PAGE_SIZE })}`),
    [JSON.stringify(filters), page],
  );
  const [exportError, setExportError] = useState<string | null>(null);
  const lastSeenFilter = filters.never_activated === 'true' ? 'never' : filters.inactive_days;
  // Al filtrar por conexión se ordena por ella (primero las que llevan más tiempo sin conectarse).
  const setLastSeenFilter = (v: string) => {
    const next = new URLSearchParams(params);
    next.delete('inactive_days');
    next.delete('never_activated');
    if (v === 'never') next.set('never_activated', 'true');
    else if (v) next.set('inactive_days', v);
    if (v && !next.get('sort')) next.set('sort', 'last_seen_at');
    if (!v && next.get('sort') === 'last_seen_at') next.delete('sort');
    next.delete('page');
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title={t('licenses.title')}
        subtitle={t('licenses.subtitle')}
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                api.download(`/v1/admin/licenses/export.csv${buildQuery(filters)}`, 'licenses.csv').catch((e) => setExportError(errorMessage(e)))
              }
            >
              {t('licenses.exportCsv')}
            </button>
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              {t('licenses.new')}
            </button>
          </>
        }
      />
      <ErrorAlert error={list.error ?? exportError} />
      <div className="card">
        <div className="toolbar">
          <input className="grow" placeholder={t('licenses.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
          <select
            value={filters.product_id}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('product_id', e.target.value);
              else next.delete('product_id');
              next.delete('edition_id');
              next.delete('page');
              setParams(next);
            }}
            aria-label={t('common.product')}
          >
            <option value="">{t('common.allProducts')}</option>
            {products.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {editionsOf(filterProduct.data).length > 0 && (
            <select value={filters.edition_id} onChange={(e) => setFilter('edition_id', e.target.value)} aria-label={t('createLicense.edition')}>
              <option value="">{t('licenses.anyEdition')}</option>
              {editionsOf(filterProduct.data).map((ed) => (
                <option key={ed.id} value={ed.id}>{ed.name}</option>
              ))}
            </select>
          )}
          <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} aria-label={t('common.status')}>
            <option value="">{t('licenses.anyStatus')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{t(`license.status.${s}`)}</option>
            ))}
          </select>
          <select value={filters.type} onChange={(e) => setFilter('type', e.target.value)} aria-label={t('common.type')}>
            <option value="">{t('licenses.anyType')}</option>
            {TYPES.map((s) => (
              <option key={s} value={s}>{t(`license.type.${s}`)}</option>
            ))}
          </select>
          <select value={filters.mode} onChange={(e) => setFilter('mode', e.target.value)} aria-label={t('createLicense.mode')}>
            <option value="">{t('licenses.anyMode')}</option>
            {MODES.map((s) => (
              <option key={s} value={s}>{t(`license.mode.${s}`)}</option>
            ))}
          </select>
          <select value={filters.expiring_within_days} onChange={(e) => setFilter('expiring_within_days', e.target.value)} aria-label={t('licenses.colExpires')}>
            <option value="">{t('licenses.anyExpiry')}</option>
            {[7, 30, 90].map((n) => (
              <option key={n} value={n}>{t('licenses.expiringIn', { n })}</option>
            ))}
          </select>
          <select value={lastSeenFilter} onChange={(e) => setLastSeenFilter(e.target.value)} aria-label={t('licenses.colLastSeen')}>
            <option value="">{t('licenses.anyLastSeen')}</option>
            {INACTIVE_DAYS.map((n) => (
              <option key={n} value={n}>{t('licenses.inactiveFor', { n })}</option>
            ))}
            <option value="never">{t('licenses.neverActivated')}</option>
          </select>
          {list.loading && <Spinner />}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('licenses.colKey')}</th>
                <th>{t('common.product')}</th>
                <th>{t('common.customer')}</th>
                <th>{t('common.type')}</th>
                <th className="num">{t('licenses.colUsage')}</th>
                <th>{t('licenses.colExpires')}</th>
                <th>{t('licenses.colLastSeen')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">{t('licenses.empty')}</td>
                </tr>
              )}
              {list.data?.items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={`/licencias/${l.id}`} className="key">{l.key}</Link>
                  </td>
                  <td>{l.product_name} {l.edition_name && <span className="tag">{l.edition_name}</span>}</td>
                  <td>{l.customer_id ? <Link to={`/clientes/${l.customer_id}`}>{l.customer_name}</Link> : <span className="muted">—</span>}</td>
                  <td><TypeBadge type={l.type} mode={l.mode} /></td>
                  <td className="num">{l.mode === 'floating' ? t('licenses.sessionsShort', { n: l.seats }) : `${l.active_activations ?? 0} / ${l.seats}`}</td>
                  <td>{fmtDate(l.expires_at)}</td>
                  <td><LastSeen at={l.last_seen_at} offlineOnly={l.offline_only} /></td>
                  <td><LicenseStatusBadge status={l.status} expiresAt={l.expires_at} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data.total}
            onPage={(p) => {
              const next = new URLSearchParams(params);
              next.set('page', String(p));
              setParams(next);
            }}
          />
        )}
      </div>
      {creating && <CreateLicenseModal products={products.data ?? []} onClose={() => setCreating(false)} defaultCustomerId={filters.customer_id} />}
    </>
  );
}

export function CreateLicenseModal({ products, onClose, defaultCustomerId }: { products: Product[]; onClose: () => void; defaultCustomerId?: string }) {
  const t = useT();
  const navigate = useNavigate();
  const [productId, setProductId] = useState(products[0]?.id ?? '');
  const product = useAsync(() => (productId ? api.get<ProductDetail>(`/v1/admin/products/${productId}`) : Promise.resolve(null)), [productId]);
  const customers = useAsync(() => api.get<Page<Customer>>('/v1/admin/customers?page_size=500'), []);
  const [type, setType] = useState<(typeof TYPES)[number]>('subscription');
  const [mode, setMode] = useState<(typeof MODES)[number]>('node_locked');
  const [seats, setSeats] = useState(1);
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? '');
  const [duration, setDuration] = useState(365);
  const [updatesUntil, setUpdatesUntil] = useState('');
  const [maxVersion, setMaxVersion] = useState('');
  const [features, setFeatures] = useState<string[] | null>(null);
  const [editionId, setEditionId] = useState('');
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const selected = features ?? product.data?.default_features ?? [];
  const editions = editionsOf(product.data);
  const edition = editions.find((e) => e.id === editionId) ?? null;
  const chooseEdition = (id: string) => {
    setEditionId(id);
    const e = editions.find((x) => x.id === id);
    setFeatures(e ? [...e.features] : null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<License>('/v1/admin/licenses', {
        product_id: productId,
        customer_id: customerId || null,
        // Sin `limits`: con edición el servidor copia los suyos; sin ella quedan vacíos, como antes.
        edition_id: editionId || null,
        type,
        mode,
        seats,
        features: selected,
        duration_days: type === 'perpetual' ? undefined : duration,
        updates_until: type === 'perpetual' && updatesUntil ? new Date(updatesUntil).toISOString() : undefined,
        max_version: maxVersion || null,
        notes: notes || null,
        notify,
      });
      navigate(`/licencias/${created.id}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('createLicense.title')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label={t('common.product')} full>
            <select value={productId} onChange={(e) => { setProductId(e.target.value); setFeatures(null); setEditionId(''); }} required>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </Field>
          <Field label={t('common.customer')} full>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">{t('createLicense.noCustomer')}</option>
              {customers.data?.items.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          {editions.length > 0 && (
            <Field
              label={t('createLicense.edition')}
              help={
                edition && Object.keys(edition.limits).length
                  ? t('createLicense.editionLimits', { limits: Object.entries(edition.limits).map(([k, v]) => `${k}=${v}`).join(', ') })
                  : t('createLicense.editionHelp')
              }
              full
            >
              <select value={editionId} onChange={(e) => chooseEdition(e.target.value)}>
                <option value="">{t('createLicense.noEdition')}</option>
                {editions.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label={t('common.type')}>
            <select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              {TYPES.map((s) => (
                <option key={s} value={s}>{t(`license.type.${s}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('createLicense.mode')} help={mode === 'floating' ? t('createLicense.modeHelpFloating') : t('createLicense.modeHelpNodeLocked')}>
            <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="node_locked">{t('createLicense.modeNodeLocked')}</option>
              <option value="floating">{t('createLicense.modeFloating')}</option>
            </select>
          </Field>
          <Field label={mode === 'floating' ? t('createLicense.sessions') : t('createLicense.seats')}>
            <input type="number" min={1} value={seats} onChange={(e) => setSeats(Number(e.target.value))} />
          </Field>
          {type === 'perpetual' ? (
            <Field label={t('createLicense.updatesUntil')} help={t('createLicense.updatesHelp')}>
              <input type="date" value={updatesUntil} onChange={(e) => setUpdatesUntil(e.target.value)} />
            </Field>
          ) : (
            <Field label={t('createLicense.duration')}>
              <input type="number" min={1} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
            </Field>
          )}
          <Field label={t('createLicense.maxVersion')} help={t('createLicense.maxVersionHelp')}>
            <input value={maxVersion} onChange={(e) => setMaxVersion(e.target.value)} placeholder={t('createLicense.noLimit')} />
          </Field>
          <Field label={t('createLicense.features')} full>
            <div className="checks">
              {product.data?.features.map((f) => (
                <label key={f.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(f.code)}
                    onChange={(e) => setFeatures(e.target.checked ? [...selected, f.code] : selected.filter((x) => x !== f.code))}
                  />
                  {f.name} <code className="muted">{f.code}</code>
                </label>
              ))}
              {product.data && product.data.features.length === 0 && <span className="muted">{t('createLicense.noFeatures')}</span>}
            </div>
          </Field>
          <Field label={t('common.notes')} full>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('createLicense.notesPlaceholder')} />
          </Field>
          <label className="checks full">
            <label>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> {t('createLicense.notify')}
            </label>
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy || !productId}>{busy && <Spinner />} {t('createLicense.submit')}</button>
        </div>
      </form>
    </Modal>
  );
}
